// src/pages/payment-links/ViewPaymentLinks.tsx
import { useEffect, useMemo, useState } from "react";
import { FiPlus, FiCheckCircle, FiAlertCircle, FiSearch } from "react-icons/fi";

import AdminLayout from "@/layouts/AdminLayout";
import Breadcrumbs from "@/components/shared/nav/Breadcrumbs";
import Modal from "@/components/shared/ui/Modal";
import ConfirmDialog from "@/components/shared/ui/ConfirmDialog";
import Field from "@/components/shared/ui/Field";
import Input from "@/components/shared/ui/Input";
import PlaneLoadingScreen from "@/components/shared/ui/PageLoadingScreen";
import TablePagination from "@/components/shared/ui/TablePagination";

import PaymentLinksTable, {
  type PaymentLinksTableFilters,
} from "@/components/payment-links/PaymentLinksTable";
import CreatePaymentLink from "@/components/payment-links/forms/CreatePaymentLink";

import {
  usePaymentLinks,
  useCreatePaymentLink,
  useResendPaymentLink,
  useCancelPaymentLink,
  useRefundPaymentLink,
} from "@/api/features/payment-links";
import type { PaymentLink } from "@/api/features/payment-links";

type Notice = { type: "success" | "error"; message: string } | null;

export default function ViewPaymentLinks() {
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [filters, setFilters] = useState<PaymentLinksTableFilters>({
    search: "",
    status: "",
  });
  const [notice, setNotice] = useState<Notice>(null);

  useEffect(() => setPage(1), [filters.search, filters.status]);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(t);
  }, [notice]);

  const query = useMemo(
    () => ({
      page,
      pageSize,
      q: filters.search?.trim() || undefined,
      status: filters.status || undefined,
    }),
    [page, filters.search, filters.status]
  );
  const { data, isLoading } = usePaymentLinks(query);

  const createMut = useCreatePaymentLink();
  const resendMut = useResendPaymentLink();
  const cancelMut = useCancelPaymentLink();
  const refundMut = useRefundPaymentLink();

  const [showCreate, setShowCreate] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createFieldErrors, setCreateFieldErrors] = useState<Record<
    string,
    string
  > | null>(null);
  const [cancelTarget, setCancelTarget] = useState<PaymentLink | null>(null);
  const [emailTarget, setEmailTarget] = useState<PaymentLink | null>(null);
  const [emailValue, setEmailValue] = useState("");
  const [refundTarget, setRefundTarget] = useState<PaymentLink | null>(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [busyId, setBusyId] = useState<number | null>(null);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  async function withBusy<T>(id: number, fn: () => Promise<T>) {
    setBusyId(id);
    try {
      return await fn();
    } finally {
      setBusyId(null);
    }
  }

  async function handleCopy(row: PaymentLink) {
    if (!row.checkoutUrl) return;
    try {
      await navigator.clipboard.writeText(row.checkoutUrl);
      setNotice({ type: "success", message: "Payment link copied to clipboard." });
    } catch {
      setNotice({ type: "error", message: "Could not copy — copy it manually." });
    }
  }

  return (
    <AdminLayout>
      <Breadcrumbs />

      {notice ? (
        <div
          className={`mb-3 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
            notice.type === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-rose-200 bg-rose-50 text-rose-700"
          }`}
        >
          {notice.type === "success" ? <FiCheckCircle /> : <FiAlertCircle />}
          {notice.message}
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Payment Links</h1>
          <p className="text-sm text-gray-500">
            Create HDFC payment links and send them to customers.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={filters.search}
              onChange={(e) =>
                setFilters((f) => ({ ...f, search: e.target.value }))
              }
              placeholder="Search code, customer, reference…"
              aria-label="Search payment links"
              className="h-[38px] w-72 rounded-lg border border-gray-300 bg-white pl-9 pr-3 text-sm outline-none placeholder:text-gray-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
            />
          </div>

          <button
            onClick={() => {
              setCreateError(null);
              setCreateFieldErrors(null);
              setShowCreate(true);
            }}
            className="inline-flex h-[38px] items-center gap-2 rounded-lg bg-sky-600 px-4 text-sm font-semibold text-white hover:bg-sky-700"
          >
            <FiPlus /> New Payment Link
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex min-h-[50vh] items-center justify-center">
          <PlaneLoadingScreen fullscreen={false} label="Loading payment links…" />
        </div>
      ) : (
        <PaymentLinksTable
          rows={items}
          filters={filters}
          setFilters={setFilters}
          busyId={busyId}
          onCopy={handleCopy}
          onResend={(r) => {
            setEmailTarget(r);
            setEmailValue(r.customerEmail ?? "");
          }}
          onCancel={(r) => setCancelTarget(r)}
          onRefund={(r) => {
            setRefundTarget(r);
            setRefundAmount("");
            setRefundReason("");
          }}
        />
      )}

      {/* Pagination — same card foot as the other list screens */}
      {!isLoading && (
        <TablePagination
          page={page}
          pageSize={pageSize}
          total={total}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}

      {/* Create modal */}
      {showCreate ? (
        <Modal
          open
          title="New Payment Link"
          onClose={() => {
            setShowCreate(false);
            setCreateError(null);
            setCreateFieldErrors(null);
          }}
        >
          <CreatePaymentLink
            submitting={createMut.isPending}
            serverError={createError}
            fieldErrors={createFieldErrors}
            onCancel={() => {
              setShowCreate(false);
              setCreateError(null);
              setCreateFieldErrors(null);
            }}
            onSubmit={(payload) => {
              setCreateError(null);
              setCreateFieldErrors(null);
              createMut
                .mutateAsync(payload)
                .then((created) => {
                  setShowCreate(false);
                  setNotice({
                    type: "success",
                    message: `Payment link ${created.linkCode} created${
                      payload.sendEmail ? " and emailed" : ""
                    }.`,
                  });
                })
                .catch((e) => {
                  // Keep the modal open and show the real, field-level error inside it.
                  setCreateError(e?.message ?? "Failed to create payment link.");
                  setCreateFieldErrors(e?.fieldErrors ?? null);
                });
            }}
          />
        </Modal>
      ) : null}

      {/* Send email modal */}
      {emailTarget ? (
        <Modal
          open
          title={`Send payment link — ${emailTarget.linkCode}`}
          onClose={() => setEmailTarget(null)}
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              The payment link for{" "}
              <span className="font-semibold text-gray-900">
                {emailTarget.currencyCode} {emailTarget.amount}
              </span>{" "}
              ({emailTarget.description}) will be emailed to the address below.
            </p>
            <Field label="Send to email *">
              <Input
                type="email"
                value={emailValue}
                onChange={(e) => setEmailValue(e.target.value)}
                placeholder="name@example.com"
              />
            </Field>
            <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-4">
              <button
                onClick={() => setEmailTarget(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                disabled={resendMut.isPending || !emailValue.trim()}
                onClick={() => {
                  const id = emailTarget.id;
                  const email = emailValue.trim();
                  if (!email) return;
                  withBusy(id, () =>
                    resendMut
                      .mutateAsync({ id, customerEmail: email })
                      .then(() => {
                        setEmailTarget(null);
                        setNotice({
                          type: "success",
                          message: `Payment link emailed to ${email}.`,
                        });
                      })
                      .catch((e) => setNotice({ type: "error", message: e.message }))
                  );
                }}
                className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-50"
              >
                {resendMut.isPending ? "Sending…" : "Send email"}
              </button>
            </div>
          </div>
        </Modal>
      ) : null}

      {/* Refund modal */}
      {refundTarget ? (
        <Modal
          open
          title={`Refund — ${refundTarget.linkCode}`}
          onClose={() => setRefundTarget(null)}
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Paid amount:{" "}
              <span className="font-semibold text-gray-900">
                {refundTarget.currencyCode} {refundTarget.amount}
              </span>
              . Leave the amount blank for a full refund.
            </p>
            <Field label="Refund amount (optional)">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                placeholder="Full refund"
              />
            </Field>
            <Field label="Reason (optional)">
              <Input
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder="Internal note"
              />
            </Field>
            <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-4">
              <button
                onClick={() => setRefundTarget(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                disabled={refundMut.isPending}
                onClick={() => {
                  const id = refundTarget.id;
                  const amt = refundAmount.trim() ? Number(refundAmount) : undefined;
                  withBusy(id, () =>
                    refundMut
                      .mutateAsync({
                        id,
                        payload: {
                          amount: amt,
                          reason: refundReason.trim() || null,
                        },
                      })
                      .then((u) => {
                        setRefundTarget(null);
                        setNotice({
                          type: "success",
                          message: `Refund issued (${u.status}).`,
                        });
                      })
                      .catch((e) => setNotice({ type: "error", message: e.message }))
                  );
                }}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
              >
                {refundMut.isPending ? "Refunding…" : "Issue refund"}
              </button>
            </div>
          </div>
        </Modal>
      ) : null}

      {/* Cancel confirm */}
      <ConfirmDialog
        open={Boolean(cancelTarget)}
        title="Cancel payment link"
        message={
          cancelTarget
            ? `Cancel "${cancelTarget.linkCode}"? The customer will no longer be able to pay with it.`
            : ""
        }
        confirmText="Cancel link"
        cancelText="Keep"
        onCancel={() => setCancelTarget(null)}
        onConfirm={() => {
          if (!cancelTarget) return;
          const id = cancelTarget.id;
          setCancelTarget(null);
          withBusy(id, () =>
            cancelMut
              .mutateAsync(id)
              .then(() => setNotice({ type: "success", message: "Payment link cancelled." }))
              .catch((e) => setNotice({ type: "error", message: e.message }))
          );
        }}
      />
    </AdminLayout>
  );
}
